import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

// f2-admin panel for the common-symbol-lists repo. Reads land in Redis
// via SymbolListsService; writes commit through the GitHub Contents API
// and the service kicks a refresh so the response carries fresh state
// without the SPA needing to poll after a save.
//
// Contract mirrors customer-assets.service.ts: Promise-returning,
// LoadingService id stack for the progress bar, ToastService on error.

export interface SymbolListManifest {
  commit_sha?: string;
  refreshed_at?: string;
  count?: string;
  source_path?: string;
  source_repo?: string;
  // Per-list file format. Populated by the backend from the registry
  // doc (mongo mode) or inferred from the file extension (legacy mode).
  // The SPA reads this to render the correct `.txt` / `.csv` badge in
  // the file-editor header (IT-F2-99).
  format?: 'txt' | 'csv';
  // CSV-only: which column name the parser uses as the ticker. Present
  // on manifests for csv-format lists; absent for txt-format lists.
  ticker_column?: string;
}

export interface SymbolListSummary {
  name: string;
  count: number;
  manifest: SymbolListManifest | null;
}

export interface SymbolListDetail {
  name: string;
  symbols: string[];
  manifest: SymbolListManifest | null;
}

// Response shape from GET /files/:name — includes sha + raw content so
// the editor can render the operator's exact bytes (comments, blanks,
// order) rather than the parsed SET which drops those.
export interface SymbolListFile {
  name: string;
  path: string;
  sha: string;
  content: string;
  symbols: string[];
}

export interface UpsertResult {
  ok: true;
  name: string;
  path: string;
  commit_sha: string;
  action: 'created' | 'updated';
  refresh: any;
}

export interface DeleteResult {
  ok: true;
  name: string;
  path: string;
  commit_sha: string;
  refresh: any;
}

@Injectable({ providedIn: 'root' })
export class CommonSymbolListsService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService,
  ) {}

  private _opts() { return { withCredentials: true }; }
  private _base() { return `${env.apiUrl}/rest/symbol-lists`; }

  async listAll(): Promise<SymbolListSummary[]> {
    const id = this.loadingService.add('Loading symbol lists');
    const url = `${this._base()}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      return Array.isArray(res?.lists) ? res.lists as SymbolListSummary[] : [];
    } catch (e: any) {
      this._toastErr('Symbol lists', e, 'F2A_SYMBOL_LISTS_LIST_FAIL', url);
      return [];
    } finally {
      this.loadingService.remove(id);
    }
  }

  async getOne(name: string): Promise<SymbolListDetail | null> {
    const id = this.loadingService.add(`Loading list ${name}`);
    const url = `${this._base()}/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      if (!res || !res.name) return null;
      return res as SymbolListDetail;
    } catch (e: any) {
      this._toastErr('Symbol lists', e, 'F2A_SYMBOL_LISTS_GETONE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  // GET /files/:name — raw file content + sha. NOT the same as getOne()
  // (which reads Redis). Use this for the editor so the operator sees
  // their comments and blank lines.
  async getFile(name: string): Promise<SymbolListFile | null> {
    const id = this.loadingService.add(`Loading file ${name}`);
    const url = `${this._base()}/files/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.get(url, this._opts()).toPromise();
      if (!res?.name) return null;
      return res as SymbolListFile;
    } catch (e: any) {
      // 404 is a valid outcome (new file — SPA proceeds to create).
      if (e?.status === 404) return null;
      this._toastErr('Symbol lists', e, 'F2A_SYMBOL_LISTS_GETFILE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  async upsertFile(name: string, content: string): Promise<UpsertResult | null> {
    const id = this.loadingService.add(`Saving ${name}`);
    const url = `${this._base()}/files/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.put(url, { content }, this._opts()).toPromise();
      if (res?.ok) return res as UpsertResult;
      return null;
    } catch (e: any) {
      this._toastErr('Save failed', e, 'F2A_SYMBOL_LISTS_UPSERT_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  async deleteFile(name: string): Promise<DeleteResult | null> {
    const id = this.loadingService.add(`Deleting ${name}`);
    const url = `${this._base()}/files/${encodeURIComponent(name)}`;
    try {
      const res: any = await this.http.delete(url, this._opts()).toPromise();
      if (res?.ok) return res as DeleteResult;
      return null;
    } catch (e: any) {
      this._toastErr('Delete failed', e, 'F2A_SYMBOL_LISTS_DELETE_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  async refresh(): Promise<any> {
    const id = this.loadingService.add('Refreshing symbol lists');
    const url = `${this._base()}/refresh`;
    try {
      const res: any = await this.http.post(url, {}, this._opts()).toPromise();
      return res;
    } catch (e: any) {
      this._toastErr('Refresh failed', e, 'F2A_SYMBOL_LISTS_REFRESH_FAIL', url);
      return null;
    } finally {
      this.loadingService.remove(id);
    }
  }

  private _toastErr(title: string, e: any, code: string, requestUrl?: string) {
    const msg = e?.error?.errors?.[0]?.message || e?.message || 'Unknown error';
    this.toast.error(title, msg, {
      error_code: code,
      customer: 'f2-admin',
      scanner_path: '/admin/common-data',
      request_url: requestUrl || e?.url || undefined,
      http_status: e?.status ?? 0,
      content_type: (e?.headers?.get?.('content-type') as string | undefined) || (e?.error && typeof e.error === 'string' ? 'text/html' : 'application/json'),
      f2_response_body: (typeof e?.error === 'string' ? e.error : JSON.stringify(e?.error || {})).slice(0, 500),
      f2_response_message: msg,
    });
  }
}
